import {useCallback, useEffect, useState} from 'react';
import {NextPage} from 'next';
import {useRouter} from 'next/router';

import {useRequestCreateAccountLink} from '#apollo/schema';
import {Button, Screen, Text} from '#component/base';
import {AuthCTAMessages} from '#component/partial';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {useAuth, useOnceEffect} from '#hook';
import {useReturnUrl} from '#hook';
import {handleRedirectToUrl} from '#hook/useReturnUrl';
import * as DESTINATION from '#hook/useReturnUrl/defaultDestination';
import {sprinkles} from '#style/sprinkles';

import {RequestCreateAccountLinkForm} from './RequestCreateAccountLinkForm';

import {
  CTAMessages,
  PageState,
  RequestCreateAccountLinkSubmitHandler,
} from './RequestCreateAccountLink.types';

const defaultCtaMessages: CTAMessages = {
  title: (
    <>
      Create your
      <br />
      account
    </>
  ),
  messages: [
    'To get you set up, enter in the e-mail address you used to purchase Tally Health products. We will e-mail you a link to create your account.',
  ],
};

export const RequestCreateAccountLink: NextPage = () => {
  const router = useRouter();
  const [renderContent, setRenderContent] = useState<boolean>(false);
  const [pageState, setPageState] = useState<PageState>('default');
  const [ctaMessages, setCtaMessages] = useState<CTAMessages>(defaultCtaMessages);
  const [successEmail, setSuccessEmail] = useState<string>('');
  const {track} = useAnalytics();
  const {isUserSignedIn} = useAuth();
  const {targetResolver} = useReturnUrl();
  const [requestCreateAccountLink] = useRequestCreateAccountLink();

  const {return_to: returnUrl} = router.query;

  const onSubmit: RequestCreateAccountLinkSubmitHandler = async ({email}) => {
    const {data} = await requestCreateAccountLink({variables: {input: {email}}});
    track<UserActionEvent>({
      evtName: EventType.UserAction,
      actionName: 'Requested Account Creation Link',
    });

    if (data?.requestCreateAccountLink && data.requestCreateAccountLink.success) {
      setSuccessEmail(email);
      setPageState('success');
      return;
    }

    // throw an error here to display an error message in the form
    if (data?.requestCreateAccountLink?.code === 'NOT_FOUND') {
      throw new Error(
        'We could not find an order associated with that e-mail, please confirm your e-mail and try again. If you continue to experience issues, please contact support.',
      );
    }
    if (data?.requestCreateAccountLink?.code === 'EXISTS') {
      throw new Error(
        'An account already exists for this e-mail address. Please sign in or reset your password.',
      );
    }
    throw new Error(
      'An unexpected error occured while requesting an account creation link. Please try again.',
    );
  };

  const resetPageState = useCallback(() => {
    setSuccessEmail('');
    setPageState('default');
  }, [setSuccessEmail, setPageState]);

  const onPageLoad = useCallback(async () => {
    const isSignedIn = await isUserSignedIn({async: true});
    const resolveReturnUrl = targetResolver(returnUrl as string);

    if (isSignedIn) {
      await handleRedirectToUrl(router, resolveReturnUrl, DESTINATION.HOME);
    } else {
      setRenderContent(true);
    }
  }, [router, isUserSignedIn, returnUrl, targetResolver]);

  useOnceEffect(() => {
    onPageLoad();
  });

  useEffect(() => {
    if (pageState === 'success') {
      setCtaMessages({
        title: (
          <>
            Create your
            <br />
            account link sent
          </>
        ),
        messages: [
          <Text key="1" variant="body" color="doveGray">
            We sent{' '}
            <Text as="span" color="inherit" fontWeight="semiBold">
              {successEmail}
            </Text>{' '}
            a link to create an account. If you did not receive a link, check your spam folder or{' '}
            <Text
              as="span"
              color="inherit"
              onClick={resetPageState}
              className={sprinkles({textDecoration: 'underline'})}>
              resend the link
            </Text>
            .
          </Text>,
          <Text key="2" variant="body" color="doveGray">
            If you continue to experience issues, you can contact{' '}
            <Text as="span" color="inherit" fontWeight="semiBold">
              hello@tallyhealth.com
            </Text>
          </Text>,
        ],
      });
    } else {
      setCtaMessages(defaultCtaMessages);
    }
  }, [successEmail, pageState, resetPageState, setCtaMessages]);

  return (
    <Screen
      contentContainerClassName={sprinkles({
        flex: 'flex',
        dir: 'col',
      })}
      pageName="Request Create Account Link"
      renderContent={renderContent}
      sidePanelImageUrl="/image/request-account-creation-link.png">
      <div
        className={sprinkles({
          direction: 'col',
          flex: 'flex',
          height: 'full',
          placeContent: ['end', 'center'],
        })}>
        <AuthCTAMessages title={ctaMessages.title} messages={ctaMessages.messages} />
        {pageState == 'default' && (
          <RequestCreateAccountLinkForm onSubmit={onSubmit} pageState={pageState} />
        )}
        {pageState === 'success' && (
          <Button variant="secondary" onClick={() => router.push('/shop')}>
            Return to Home
          </Button>
        )}
      </div>
    </Screen>
  );
};
